import Vue from 'vue';
import axios from 'axios';
import VueAxios from 'vue-axios';
import App from '@/App.vue';
import router from '@/router';
import store from '@/store';
import stationPinColor from '@/lib/stationPinColor';
import stationPinLetter from '@/lib/stationPinLetter';
import FontAwesomeIcon from '@/lib/FontAwesomeIcon';
import FormatNumber from '@/lib/FormatNumber';
import FormatDate from '@/lib/FormatDate';
import GoogleAnalytics from '@/lib/GoogleAnalytics';
import I18n from '@/lib/I18n';
import Rollbar from '@/lib/Rollbar';
import VeeValidate from 'vee-validate';
import VueTippy from 'vue-tippy';

Vue.config.productionTip = false;
Vue.use(VueTippy);
Vue.mixin({
  methods: {
    stationCityStateZip(city, state, zip) {
      let cityStateZip = null;
      if (city && state && zip) {
        // Note: We replace spaces in the zip with a non-breaking space to
        // ensure Canadian zips are always displayed on the same line (rather
        // than possibly being wrapped and split between lines).
        cityStateZip = `${city}, ${state} ${zip.replace(' ', '\xa0')}`;
      }
      return cityStateZip;
    },

    stationDirections(lat, long, street, cityStateZip) {
      const combinedAddress = `${street}, ${cityStateZip}`;

      return `https://www.google.com/maps/dir//${combinedAddress.replace(/\s/g, '+').replace(/#/g, '%23')}/@${lat},${long}z`;
    },

    stationPhoneNumbers(numbers) {
      return numbers.split(/(\s+)/).filter((n) => n.trim().length > 0);
    },

    stationEvData(level1, level2, dcFast) {
      return [
        { text: this.$t('Level 1'), number: level1 },
        { text: this.$t('Level 2'), number: level2 },
        { text: this.$t('DC Fast'), number: dcFast },
      ];
    },

    stationEvLevels(level1, level2, dcFast) {
      let stationLevels = null;
      if (level1 || level2 || dcFast) {
        const levels = [];
        if (level1) { levels.push(this.$t('Level 1')); }
        if (level2) { levels.push(this.$t('Level 2')); }
        if (dcFast) { levels.push(this.$t('DC Fast')); }
        stationLevels = levels.join(', ');
      }
      return stationLevels;
    },

    stationEvNetworks(networks) {
      const sortedNetworks = networks.filter((network) => network.key !== 'Non-Networked');
      sortedNetworks.push({ key: 'Non-Networked', name: this.$t('Non-Networked') });
      return sortedNetworks;
    },

    stationEvNetworksByKey(networks) {
      const networkArray = [];
      networks.forEach((network) => {
        if (network === 'Non-Networked') {
          networkArray.push(this.$t('Non-Networked'));
        } else {
          networkArray.push(network);
        }
      });
      return networkArray.sort().join(', ');
    },

    stationEvConnectors(connector, field) {
      const evConnectors = {
        NEMA515: {
          level: 'Level 1',
          label: this.$t('NEMA 5-15'),
          image: 'level-1-2',
        },
        NEMA520: {
          level: 'Level 1',
          label: this.$t('NEMA 5-20'),
          image: 'level-1-2',
        },
        NEMA1450: {
          level: 'Level 1',
          label: this.$t('NEMA 14-50'),
          image: 'level-1-2',
        },
        J1772: {
          level: 'Level 2',
          label: this.$t('J1772'),
          image: 'level-1-2',
        },
        J1772COMBO: {
          level: 'DC Fast',
          label: this.$t('CCS'),
          image: 'dc-sae',
        },
        TESLA: {
          level: 'DC Fast',
          label: this.$t('NACS'),
          image: 'dc-tesla',
        },
        CHADEMO: {
          level: 'DC Fast',
          label: this.$t('CHAdeMO'),
          image: 'dc-chademo',
        },
      };
      return evConnectors[connector][field];
    },

    stationPinColor,

    stationPinLetter,
  },
});

if (!window.afdcStationsOptions) {
  window.afdcStationsOptions = {};
}

// Set the API key used for developer.nrel.gov API calls based on the
// environment.
//
// Use separate keys for development and staging. For production, use separate
// keys for the main AFDC and Canada sites (with HTTP referer restrictions, to
// prevent casual reuse), and separate keys for US and Canadian external widget
// usage (without HTTP referer restrictions).
const { hostname } = window.location;
if (!window.afdcStationsOptions.apiKey) {
  if (process.env.VUE_APP_ENV === 'development') {
    window.afdcStationsOptions.apiKey = process.env.VUE_APP_API_KEY_DEV;
  } if (process.env.VUE_APP_ENV === 'test') {
    window.afdcStationsOptions.apiKey = process.env.VUE_APP_API_KEY_TEST;
  } else if (process.env.VUE_APP_ENV !== 'production') {
    if (hostname.includes('afdc-stage.tada-stage.nrel.gov')) {
      window.afdcStationsOptions.apiKey = process.env.VUE_APP_API_KEY_STAGING;
    } else {
      window.afdcStationsOptions.apiKey = process.env.VUE_APP_API_KEY_STAGING_WIDGET;
    }
  } else if (hostname.includes('afdc.energy.gov')) {
    window.afdcStationsOptions.apiKey = process.env.VUE_APP_API_KEY_AFDC;
  } else if (hostname.includes('natural-resources.canada.ca') || hostname.includes('ressources-naturelles.canada.ca') || hostname.includes('nrcan.gc.ca') || hostname.includes('rncan.gc.ca')) {
    window.afdcStationsOptions.apiKey = process.env.VUE_APP_API_KEY_NRCAN;
  } else if (window.afdcStationsOptions.localeCountry === 'CA') {
    window.afdcStationsOptions.apiKey = process.env.VUE_APP_API_KEY_NRCAN_WIDGET;
  } else {
    window.afdcStationsOptions.apiKey = process.env.VUE_APP_API_KEY_AFDC_WIDGET;
  }
}

if (hostname.includes('nrcan.gc.ca') || hostname.includes('rncan.gc.ca') || window.afdcStationsOptions.localeCountry === 'CA') {
  window.afdcStationsOptions.corridor = false;
}

if (!window.afdcStationsOptions.maptilerKey) {
  if (process.env.VUE_APP_ENV === 'development') {
    window.afdcStationsOptions.maptilerKey = process.env.VUE_APP_MAPTILER_KEY_DEV;
  } else if (process.env.VUE_APP_ENV === 'test') {
    window.afdcStationsOptions.maptilerKey = process.env.VUE_APP_MAPTILER_KEY_TEST;
  } else if (process.env.VUE_APP_ENV !== 'production') {
    if (hostname.includes('afdc-stage.tada-stage.nrel.gov')) {
      window.afdcStationsOptions.maptilerKey = process.env.VUE_APP_MAPTILER_KEY_STAGING;
    } else {
      window.afdcStationsOptions.maptilerKey = process.env.VUE_APP_MAPTILER_KEY_STAGING_WIDGET;
    }
  } else if (hostname.includes('afdc.energy.gov')) {
    window.afdcStationsOptions.maptilerKey = process.env.VUE_APP_MAPTILER_KEY_AFDC;
  } else if (hostname.includes('nrcan.gc.ca') || hostname.includes('rncan.gc.ca')) {
    window.afdcStationsOptions.maptilerKey = process.env.VUE_APP_MAPTILER_KEY_NRCAN;
  } else if (window.afdcStationsOptions.localeCountry === 'CA') {
    window.afdcStationsOptions.maptilerKey = process.env.VUE_APP_MAPTILER_KEY_NRCAN_WIDGET;
  } else {
    window.afdcStationsOptions.maptilerKey = process.env.VUE_APP_MAPTILER_KEY_AFDC_WIDGET;
  }
}
if ((hostname.includes('localhost'))) {
  window.afdcStationsOptions.corridor = true;
}

const tadaAnalyticsOptions = {
  env: process.env.VUE_APP_ENV,

  // Disable the default analytics pageview from being sent on first load,
  // since we will handle all of our reporting (including the initial pageview)
  // via the Vue router.afterEach integration (where we will also track all Vue
  // route changes).
  sendInitialPageview: false,
};
if (window.afdcStationsOptions.localeCountry === 'CA') {
  tadaAnalyticsOptions.ids = [
    'UA-5743150-12', // TADA Team - NRCan
  ];
  // We don't want Canada traffic to be reported to the US-wide government DAP
  // account.
  tadaAnalyticsOptions.enableUsGovWideTracker = false;
} else {
  tadaAnalyticsOptions.ids = [
    'UA-24103967-1', // EERE Rollup
    'UA-25110724-1', // EERE Vehicles
    'UA-5743150-13', // TADA Team - AFDC
  ];
}

// For any non-AFDC site, scope the analytics link tracking so that it only
// listens for links within our widget container. This prevents us from
// listening on all click events outside our widget when embedded on other
// websites.
if (!hostname.includes('afdc.energy.gov') && !hostname.includes('afdc-stage.tada-stage.nrel.gov')) {
  tadaAnalyticsOptions.outboundLinkTrackerOptions = {
    linkSelector: '#afdc-stations a, #afdc-stations area',
  };
}

// For embedding on NRCan's website, prevent the NRCan's other JavaScript from
// trying to parse the hash fragment, which generates JavaScript errors. This
// is caused by their template's hash parsing, done by the wet-boew library:
// https://github.com/wet-boew/wet-boew
if (window.wb && typeof window.wb === 'object') {
  window.wb.ignoreHashChange = true;
}

Vue.use(Rollbar);
Vue.use(GoogleAnalytics, {
  tadaAnalyticsOptions,
  router,
  store,
});
Vue.use(I18n);
Vue.use(VueAxios, axios);
Vue.use(FormatNumber);
Vue.use(FormatDate);
Vue.use(FontAwesomeIcon);
Vue.use(VeeValidate);

new Vue({
  router,
  store,
  render: (h) => h(App),

  created() {
    if (window.afdcStationsOptions.path || window.afdcStationsOptions.query) {
      this.$router.replace({
        path: window.afdcStationsOptions.path,
        query: window.afdcStationsOptions.query,
      });
    }

    if ((hostname.includes('localhost'))) {
      this.$store.commit('setDisplayDirections', true);
    }

    this.$store.dispatch('fetchLoaderData');
    this.$store.dispatch('generateMapImageData');
  },
}).$mount('#afdc-stations');
